import React, {createRef, useState, useContext} from 'react';
import { DragDropContext, Droppable, Draggable } from '@react-forked/dnd';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";

import MaterialTable from "@material-table/core";
import MaterialTableBody from "../../../controls/Material-Table-Components/MaterialTableBody";
import MaterialTableBodyRow from "../../../controls/Material-Table-Components/MaterialTableBodyRow";

import SystemContext from "../../../SystemContext";
import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import AirportNotamValidator from "../validators/AirportNotamValidator";
import {ComboSelect, MaskedTime, DatePicker, MaskedText} from '../../../controls';
import { useAirportNotams, useAddAirportNotam, useDeleteAirportNotam, useUpdateAirportNotam } from "../../../services/airports";
import {Checkbox, Tooltip, useTheme} from "@material-ui/core";
import {defaultComponents} from "../../../controls/Material-Table-Components";

const dateFormat = "DD/MM/YYYY";


const AirportNotamLineEditor = (_props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();


  const props = {
    ..._props
  }
  const {classes} = props;

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const airportCode = props.airportCode

  const handleAddNotam = useAddAirportNotam();
  const handleUpdateNotam = useUpdateAirportNotam();
  const handleDeleteNotam = useDeleteAirportNotam();

  const queryClient = useQueryClient();
  const extraKeys = [airportCode];
  const airportNotams = useAirportNotams({tableRef, queryClient, extraKeys});

  const minDate = moment.utc();
  const initialDate = minDate.toISOString();

  const eventBus = SystemData.eventBus;
  if (eventBus) {
    eventBus.bind('AirportNotam.AddLine', (action)=> {
      if (tableRef && tableRef.current) {
        if (action && action === 'edit') {
          setState({...state, canEdit: true});
        } else if (action && action === 'cancel') {
          if (tableRef.current.state.showAddRow) {
            tableRef.current.dataManager.changeRowEditing();
            tableRef.current.setState({
              ...tableRef.current.dataManager.getRenderState(),
              showAddRow: !tableRef.current.state.showAddRow,
            });
          }
          setState({...state, canEdit: false});
        } else {
          tableRef.current.dataManager.changeRowEditing();
          tableRef.current.setState({
            ...tableRef.current.dataManager.getRenderState(),
            showAddRow: !tableRef.current.state.showAddRow,
          });
          setState({...state, canEdit: true});
        }
      }
    });
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: state.canEdit?"always":"never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const formatChars={
      "U": "[A-Z]",
      "X": "[A-Za-z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9]-",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const validatorColumn = ValidatorColumn(AirportNotamValidator, false);

    return [
      {...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(140,[5,2],undefined,undefined,undefined,undefined,false),
        title: "NOTAM",
        field: "notamCode",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        fullWidth: true,
        sorting: true,
        render: rowData => (
          <ComboSelect
            group="NOTAM"
            subGroup=""
            value={rowData.notamCode}
            displayOnly={true}
          />
        ),
        editComponent: (props) => {
          return (
            <ComboSelect
              group="NOTAM"
              subGroup=""
              // fullWidth
              value={props.value}
              onChange={props.onChange}
            />
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(130,2,undefined, {paddingTop: 10},undefined,undefined,false),
        title: "Effective From",
        field: "notamFromDate",
        sorting: false,
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            initialValue={minDate}
            minDate={props.value || minDate}
            defaultValue={initialDate}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <div>{moment(rowData.notamFromDate).format(dateFormat)}</div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(70,2,undefined,undefined,undefined,undefined,false),
        title: "At Hour (UTC)",
        field: "notamFromTime",
        type: "time",
        emptyValue: "",
        defaultValue: "",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: rowData => (
          <div>{rowData.notamFromTime?.substr(0,Math.min(rowData.notamFromTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(130,2,undefined,{paddingTop: 10},undefined,undefined,false),
        title: "Effective To",
        field: "notamToDate",
        sorting: false,
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            initialValue={props.rowData.notamFromDate || initialDate}
            minDate={props.rowData.notamFromDate || minDate}
            defaultValue={props.rowData.notamFromDate || initialDate}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <div>{moment(rowData.notamToDate).format(dateFormat)}</div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(70,2,undefined,undefined,undefined,undefined,false),
        title: "At Hour (UTC)",
        field: "notamToTime",
        type: "time",
        emptyValue: "",
        defaultValue: "",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: rowData => (
          <div>{rowData.notamToTime?.substr(0,Math.min(rowData.notamToTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70,2,undefined,undefined,undefined,undefined,false),
        title: "Recurring",
        field: "recurring",
        type: "boolean",
        render: rowData => (
          <Tooltip title={"'Recurring' NOTAMs will be effective on every day listed in 'Days Effective' between the NOTAM hours during the period specified by the 'Effective From' and 'Effective To' dates. Non-recurring NOTAMs operate during every daya dn every hour between the 'Effective From' and 'Effective To' dates."}>
            <Checkbox
              style={{padding: 0}}
              checked={(rowData.recurring === 1 || rowData.recurring === true) ?? true}
            />
          </Tooltip>
        ),
        editComponent: (props) => (
          <Checkbox
            style={{padding: 0}}
            checked={(props.rowData.recurring === 1 || props.rowData.recurring === true) ?? true}
            onChange={ (event, checked) => {
              const notamDays = checked ? props.rowData.previousNotamDays ?? "" : "";
              const previousNotamDays = props.rowData.notamDays ?? "";
              const recurring = checked;
              let data = { ...props.rowData, recurring, previousNotamDays, notamDays };
              props.onRowDataChange(data);
            }}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100,2,undefined,undefined,undefined,undefined,false),
        title: "Days Effective",
        field: "notamDays",
        editable: ((cell, rowData, updatedData) => {
          return updatedData ? updatedData.recurring : rowData.recurring;
        }),
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(7)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={{ x: "[1-7]" }}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Direction",
        field: "direction",
        render: (rowData) => (
          <ComboSelect
            id="direction"
            group="DIRECTION"
            subGroup="NOTAM"
            fullWidth
            value={rowData.direction}
            displayOnly={true}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="direction"
            group="DIRECTION"
            subGroup="NOTAM"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(undefined,2,undefined,{width: "100%"},undefined,undefined,false),
        title: "Details",
        field: "notamDetails",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(250)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
    ];
  };

  const onDragEnd = (dragProps) => {
    return;
  }


  const bodyRef = createRef();

  return (isAuthenticated &&
        <div className={classes.notamLineEditor}>
          <MaterialTable
            tableRef={tableRef}
            data={airportNotams.data}
            style={{
              backgroundColor: "#fdfdfd",
              boxShadow: "none",
              border: "0px #aaaaaa",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              search: false,
              cellStyle: {
                padding: "8px 2px",
              },
              headerRowStyle: {
                display: "table",
                width: "100%",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              showTitle: false,
              toolbar: false,
              padding: 'dense',
              paging: false,
              filtering: false,
              draggable: false,
              addRowPosition: 'first',
              onEditModeChanged: (editMode)=> {
                if (props.changeButtonState) {
                  const changes = {
                    save: {disabled: false, hidden: false},
                    send: {disabled: false, hidden: false},
                  }
                  if (editMode) {
                    changes['save'] = {disabled: true, hidden: false}
                    changes['send'] = {disabled: true, hidden: false}
                  }
                  props.changeButtonState(changes);
                }
              },
            }}
            components={{
              ...defaultComponents,
              Body: (props) => (
                <MaterialTableBody
                  {...props}
                  ref={bodyRef}
                  rowHeight={46}
                  overscanRowCount={10}
                  addVirtualScrolling={true}
                  bodyHeight={80}
                  headerHeight={46}
                />
              ),
              Row: (props) => (
                <MaterialTableBodyRow
                  {...props}
                />
              ),
            }}
            title="Airport Notams"
            columns={getColumns({...props, canEdit: state.canEdit})}
            actions={[
            ]}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                ? (newData) => new Promise((resolve, reject) => {
                  newData["airportCode"] = airportCode;
                  handleAddNotam.mutate({record: newData, resolve, reject, extraKeys});
                })
                : undefined,
              onRowUpdate: (newData, oldData) =>
                new Promise((resolve, reject) => {
                  handleUpdateNotam.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                }),
              onRowDelete: (oldData) =>
                new Promise((resolve, reject) => {
                  handleDeleteNotam.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                }),
            }}
        />
      </div>
  );
}

export default AirportNotamLineEditor;
